import { api, type ApiError } from "./client";
import { useResource } from "./useResource";

// Operator-only view + control of the background-jobs queue. Staff-gated on the
// server (403 for non-staff). See specifications/background-jobs.md.

export type ApiJob = {
  readonly attempts: number;
  readonly created_at: string;
  readonly dedup_key: string | null;
  readonly finished_at: string | null;
  readonly kind: string;
  readonly last_attempt_at: string | null;
  readonly last_error: string | null;
  readonly max_attempts: number;
  readonly public_id: string;
  readonly run_at: string;
  readonly state: string;
};

export type ApiJobCount = {
  readonly kind: string;
  readonly n: number;
  readonly state: string;
};

export type JobsListResponse = {
  readonly counts: ReadonlyArray<ApiJobCount>;
  readonly jobs: ReadonlyArray<ApiJob>;
};

export type UseJobs = {
  readonly counts: ReadonlyArray<ApiJobCount>;
  readonly error: ApiError | undefined;
  readonly jobs: ReadonlyArray<ApiJob>;
  readonly loading: boolean;
  readonly refetch: () => void;
};

// `enabled` skips the fetch entirely for non-staff viewers (so we never 403).
export const useJobs = (enabled: boolean, state?: string): UseJobs => {
  const { data, error, loading, refetch } = useResource<JobsListResponse>(
    enabled ? "/jobs" : null,
    state ? { state } : undefined,
  );
  return {
    counts: data?.counts ?? [],
    error,
    jobs: data?.jobs ?? [],
    loading,
    refetch,
  };
};

export const cancelJob = (id: string): Promise<{ outcome: string }> =>
  api.post(`/jobs/${id}/cancel`);

export const retryJob = (id: string): Promise<{ ok: boolean }> =>
  api.post(`/jobs/${id}/retry`);
